// Tremor Raw Label [v0.0.1]

import { Label as LabelPrimitives } from 'radix-ui'
import { createNonNullableContext, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import type { ComponentProps, ReactElement } from 'react'

export const labelVariants = tv({
  base: [
    // base
    'flex cursor-pointer items-center justify-between self-stretch leading-none',
    // text color
    'text-tremor-content data-[error=true]:text-red-500',
  ],

  defaultVariants: {
    disabled: false,
    isRequired: false,
    size: 'md',
  },

  slots: {
    content: 'inline-flex items-center gap-x-1.5',
  },

  variants: {
    disabled: {
      true: 'text-tremor-content-subtle',
    },
    isRequired: {
      true: {
        content:
          'inline-flex items-center gap-x-1.5 after:ml-1 after:text-red-500 after:content-["*"]',
      },
    },
    size: {
      lg: 'text-base',
      md: 'text-sm',
      sm: 'text-xs',
    },
    wrap: {
      nowrap: 'whitespace-nowrap',
    },
  },
})

const LabelClassNamesContext = createNonNullableContext<
  ReturnType<typeof labelVariants>
>('LabelClassNamesContext')

export type LabelProps = Omit<
  ComponentProps<typeof LabelPrimitives.Root>,
  'className'
> &
  VariantProps<typeof labelVariants> & {
    endAdornment?: ReactElement | null
  }

function Label({
  children,
  disabled,
  endAdornment,
  isRequired,
  size,
  wrap,
  ...props
}: LabelProps) {
  const classNames = labelVariants({
    disabled,
    isRequired,
    size,
    wrap,
  })

  return (
    <LabelClassNamesContext.Context value={classNames}>
      <LabelPrimitives.Root
        aria-disabled={disabled}
        {...props}
        className={classNames.base()}
      >
        <LabelContent>{children}</LabelContent>
        {endAdornment}
      </LabelPrimitives.Root>
    </LabelClassNamesContext.Context>
  )
}

export type LabelContentProps = Omit<ComponentProps<'span'>, 'className'>

function LabelContent(props: LabelContentProps) {
  const classNames = LabelClassNamesContext.useRequiredContext()

  return <span className={classNames.content()} {...props} />
}

export { Label, LabelContent }
